if (!customElements.get('pinned-horizontal-gallery')) {
  class PinnedHorizontalGallery extends HTMLElement {
    constructor() {
      super();

      this.pin = null;
      this.track = null;
      this.cards = [];
      this.media = [];
      this.indicatorButtons = [];
      this.tween = null;
      this.scrollTrigger = null;
      this.resizeObserver = null;
      this.abortController = null;
      this.desktopQuery = window.matchMedia('(min-width: 1280px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;
      this.initTimer = null;
      this.refreshFrame = null;
      this.retryCount = 0;
      this.dragState = null;
      this.observedWidth = 0;
    }

    connectedCallback() {
      if (this.abortController) return;

      this.pin = this.querySelector('[data-gallery-pin]');
      this.track = this.querySelector('[data-gallery-track]');
      this.cards = Array.from(this.querySelectorAll('[data-gallery-card]'));
      this.media = Array.from(this.querySelectorAll('[data-gallery-media]'));
      this.indicatorButtons = Array.from(this.querySelectorAll('[data-gallery-index]'));

      if (!this.pin || !this.track || this.cards.length === 0) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.desktopQuery.addEventListener('change', this.handleModeChange, { signal });
      this.reducedMotionQuery.addEventListener('change', this.handleModeChange, { signal });
      this.pin.addEventListener('keydown', this.handleKeydown, { signal });
      this.pin.addEventListener('pointerdown', this.handlePointerDown, { signal });
      this.pin.addEventListener('pointermove', this.handlePointerMove, { signal });
      this.pin.addEventListener('pointerup', this.handlePointerEnd, { signal });
      this.pin.addEventListener('pointercancel', this.handlePointerEnd, { signal });
      this.pin.addEventListener('lostpointercapture', this.handlePointerEnd, { signal });

      this.indicatorButtons.forEach((button) => {
        button.addEventListener('click', this.handleIndicatorClick, { signal });
      });

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this);
      this.setup();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      window.clearTimeout(this.initTimer);
      window.cancelAnimationFrame(this.refreshFrame);
      this.destroyDesktop();
    }

    handleModeChange = () => {
      this.retryCount = 0;
      this.setup();
    };

    handleResize = (entries) => {
      if (!this.scrollTrigger) return;

      const width = Math.round(entries[0]?.contentRect.width || this.clientWidth);
      if (width === this.observedWidth) return;
      this.observedWidth = width;

      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.scrollTrigger?.refresh();
      });
    };

    setup() {
      window.clearTimeout(this.initTimer);
      this.initTimer = null;
      this.destroyDesktop();

      if (!this.desktopQuery.matches || this.reducedMotionQuery.matches || this.cards.length < 2) {
        this.updateActive(0);
        return;
      }

      // Apply the desktop geometry immediately instead of waiting for the
      // asynchronously loaded GSAP bundle. Otherwise the gallery can remain a
      // tall vertical list until the visitor reaches it, then collapse into a
      // pinned horizontal panel and visibly shift the page.
      this.classList.add('is-enhanced');
      this.waitForGsap();
    }

    waitForGsap() {
      if (!this.isConnected || !this.desktopQuery.matches || this.reducedMotionQuery.matches) return;

      if (!window.gsap || !window.ScrollTrigger) {
        if (this.retryCount >= 60) {
          this.classList.remove('is-enhanced');
          this.updateActive(0);
          return;
        }

        this.retryCount += 1;
        this.initTimer = window.setTimeout(() => this.waitForGsap(), 100);
        return;
      }

      this.retryCount = 0;
      window.gsap.registerPlugin(window.ScrollTrigger);

      const scrub = Number.parseFloat(this.dataset.galleryScrub) || 1.2;
      const scrollFactor = Number.parseFloat(this.dataset.galleryScrollFactor) || 1.4;

      this.tween = window.gsap.fromTo(this.track, {
        x: () => this.getCardCenterOffset(0),
      }, {
        x: () => this.getCardCenterOffset(this.cards.length - 1),
        ease: 'none',
        overwrite: true,
        scrollTrigger: {
          trigger: this.pin,
          pin: true,
          start: () => `top top+=${this.getHeaderOffset()}px`,
          end: () => `+=${Math.max(this.getHorizontalDistance() * scrollFactor, window.innerHeight)}`,
          scrub,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (instance) => this.handleProgress(instance.progress),
          onRefresh: (instance) => this.handleProgress(instance.progress),
        },
      });

      this.scrollTrigger = this.tween.scrollTrigger;
      this.handleProgress(0);
    }

    destroyDesktop() {
      const trigger = this.scrollTrigger || this.tween?.scrollTrigger;
      if (trigger && typeof trigger.kill === 'function') trigger.kill(true);
      if (this.tween && typeof this.tween.kill === 'function') this.tween.kill();

      this.scrollTrigger = null;
      this.tween = null;
      this.dragState = null;
      this.observedWidth = Math.round(this.clientWidth);
      this.classList.remove('is-enhanced', 'is-dragging');

      if (this.track) this.track.style.removeProperty('transform');
      this.media.forEach((item) => item.style.removeProperty('--pinned-gallery-parallax-x'));
    }

    getHeaderOffset() {
      const styles = getComputedStyle(this);
      const stickyArea = Number.parseFloat(styles.getPropertyValue('--pinned-gallery-header-offset'));
      if (Number.isFinite(stickyArea) && stickyArea > 0) return Math.round(stickyArea);

      const rootStyles = getComputedStyle(document.documentElement);
      const headerHeight = Number.parseFloat(rootStyles.getPropertyValue('--header-height'));
      return Math.round(Number.isFinite(headerHeight) && headerHeight > 0 ? headerHeight : 60);
    }

    /**
     * Returns the X offset that centers a card in the pinned viewport. Keeping
     * this geometry in JavaScript avoids relying on intrinsic track padding,
     * which can differ once ScrollTrigger adds its pin spacer.
     *
     * @param {number} index Card index to center.
     * @returns {number} Horizontal translate offset.
     */
    getCardCenterOffset(index) {
      const card = this.cards[index];
      if (!card || !this.pin) return 0;

      return this.pin.clientWidth / 2 - (card.offsetLeft + card.offsetWidth / 2);
    }

    /**
     * Distance required to advance from the first card center to the last.
     * This remains independent of the track's leading and trailing padding.
     */
    getHorizontalDistance() {
      if (this.cards.length < 2) return 0;

      return Math.abs(
        this.getCardCenterOffset(this.cards.length - 1) - this.getCardCenterOffset(0),
      );
    }

    getClosestCardIndex() {
      const lastIndex = this.cards.length - 1;
      if (lastIndex <= 0) return 0;

      const currentX = window.gsap ? Number.parseFloat(window.gsap.getProperty(this.track, 'x')) || 0 : 0;
      const pinCenter = this.pin.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2 + currentX;
        const distance = Math.abs(cardCenter - pinCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    handleProgress(progress) {
      const lastIndex = this.cards.length - 1;
      this.updateActive(Math.round(progress * lastIndex));

      this.media.forEach((item, index) => {
        const cardProgress = lastIndex > 0 ? index / lastIndex : 0;
        const relativeProgress = Math.max(-1, Math.min(1, (cardProgress - progress) * lastIndex));
        // The 1.04 image scale provides roughly 2% overscan per inline edge.
        // Keep the parallax below that allowance so scrubbing never exposes
        // the media background as a black strip.
        item.style.setProperty('--pinned-gallery-parallax-x', `${relativeProgress * 1.5}%`);
      });
    }

    updateActive(index) {
      this.activeIndex = Math.max(0, Math.min(index, this.cards.length - 1));

      this.cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === this.activeIndex);
        const mediaActive = !this.scrollTrigger || cardIndex === this.activeIndex;
        const video = card.querySelector('story-video');
        if (video && video.dataset.mediaActive !== String(mediaActive)) {
          video.setAttribute('data-media-active', String(mediaActive));
        }
      });

      this.indicatorButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === this.activeIndex;
        button.classList.toggle('is-active', isActive);
        if (isActive) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      this.loadNearbyMedia();
    }

    /**
     * Native lazy loading only considers vertical document proximity. Promote
     * the active slide and its neighbours once the pinned gallery is in use so
     * horizontally translated media never remains a transparent placeholder.
     */
    loadNearbyMedia() {
      this.media.forEach((media, index) => {
        if (Math.abs(index - this.activeIndex) > 1) return;

        media.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          image.loading = 'eager';
        });
      });
    }

    /**
     * Scrolls the pinned gallery to a card.
     * Native `window.scrollTo({ behavior: 'smooth' })` is unreliable with a
     * scrubbed pin, so the ScrollTrigger scroller is updated directly.
     *
     * @param {number} index Target card index.
     */
    goToIndex(index) {
      if (!this.scrollTrigger) return false;

      const targetIndex = Math.max(0, Math.min(index, this.cards.length - 1));
      const lastIndex = this.cards.length - 1;
      const progress = lastIndex > 0 ? targetIndex / lastIndex : 0;
      const start = this.scrollTrigger.start;
      const end = this.scrollTrigger.end;
      if (!Number.isFinite(start) || !Number.isFinite(end)) return false;

      this.scrollTrigger.scroll(start + (end - start) * progress);
      this.tween?.progress(progress);
      this.updateActive(targetIndex);
      return true;
    }

    handleIndicatorClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const index = Number.parseInt(event.currentTarget.dataset.galleryIndex, 10);
      if (!Number.isNaN(index)) this.goToIndex(index);
    };

    handleKeydown = (event) => {
      if (!this.scrollTrigger || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.goToIndex(this.activeIndex + direction);
    };

    handlePointerDown = (event) => {
      if (!this.scrollTrigger || event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('button, a, input, select, textarea')) return;

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: window.scrollY,
      };
      this.classList.add('is-dragging');
      this.pin.setPointerCapture(event.pointerId);
      event.preventDefault();
    };

    handlePointerMove = (event) => {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.scrollTrigger) return;

      const horizontalDistance = Math.max(this.getHorizontalDistance(), 1);
      const scrollDistance = this.scrollTrigger.end - this.scrollTrigger.start;
      const scrollPerPixel = scrollDistance / horizontalDistance;
      const deltaX = event.clientX - this.dragState.startX;
      window.scrollTo(0, this.dragState.startScroll - deltaX * scrollPerPixel);
    };

    handlePointerEnd = (event) => {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      if (this.pin.hasPointerCapture(event.pointerId)) this.pin.releasePointerCapture(event.pointerId);
      this.dragState = null;
      this.classList.remove('is-dragging');
    };
  }

  customElements.define('pinned-horizontal-gallery', PinnedHorizontalGallery);
}
